import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getImgUrl = (url) => {
  if (!url || url === 'None' || url === 'null') return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/uploads/')) return `${BACKEND_URL}${url}`;
  return url;
};

function Profile() {
  const [profile, setProfile] = useState({ first_name: '', last_name: '', birthday: '', phone: '', city: '', profile_photo: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [subscription, setSubscription] = useState(null);
  const [settings, setSettings] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/'); return; }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    Promise.all([
      axios.get(`${API}/auth/me`),
      axios.get(`${API}/subscription/status`).catch(() => ({ data: null })),
      axios.get(`${API}/settings`).catch(() => ({ data: null })),
    ]).then(([meRes, subRes, settingsRes]) => {
      setProfile({
        first_name: meRes.data.first_name || '',
        last_name: meRes.data.last_name || '',
        birthday: meRes.data.birthday || '',
        phone: meRes.data.phone || '',
        city: meRes.data.city || '',
        profile_photo: meRes.data.profile_photo || '',
      });
      if (subRes.data) setSubscription(subRes.data);
      if (settingsRes.data) setSettings(settingsRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [navigate]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setMessage('');
    try {
      await axios.put(`${API}/profile`, profile);
      setMessage('Profile saved!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) { setMessage('Error saving profile'); }
    finally { setSaving(false); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('File too large! Max 5MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/profile/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.profile_photo) setProfile(prev => ({ ...prev, profile_photo: res.data.profile_photo }));
      else if (res.data.photo_url) setProfile(prev => ({ ...prev, profile_photo: res.data.photo_url }));
    } catch (error) { alert('Error uploading photo'); }
    finally { setUploading(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage('');
    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordMessage('New passwords do not match');
      return;
    }
    if (passwordData.new_password.length < 6) {
      setPasswordMessage('New password must be at least 6 characters');
      return;
    }
    setPasswordLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      setPasswordMessage('Password changed successfully!');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      setShowChangePassword(false);
    } catch (err) {
      setPasswordMessage(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e27] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0e27]" data-testid="profile-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#0a0e27]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-white/40 hover:text-white text-sm font-bold transition-colors" data-testid="back-to-dashboard">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" /></svg>
            Back
          </button>
          <h1 className="text-sm font-extrabold text-white tracking-tight">My Profile</h1>
          <div className="w-12"></div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-8">
        {/* Photo */}
        <div className="text-center mb-8">
          <div className="relative inline-block">
            {getImgUrl(profile.profile_photo) ? (
              <img
                src={getImgUrl(profile.profile_photo)}
                alt="Profile"
                className="w-24 h-24 rounded-2xl object-cover border-2 border-emerald-500/20"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="w-24 h-24 rounded-2xl bg-white/5 border-2 border-white/10 flex items-center justify-center">
                <svg className="w-10 h-10 text-white/30" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" /></svg>
              </div>
            )}
            <label className="absolute -bottom-2 -right-2 w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center cursor-pointer hover:brightness-110 transition-all">
              {uploading ? (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              ) : (
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" /></svg>
              )}
              <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} disabled={uploading} />
            </label>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSave} className="bg-white/5 rounded-2xl border border-white/10 p-6 space-y-5">
          {message && (
            <div className={`px-4 py-3 rounded-lg text-sm font-bold ${message.includes('Error') ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'}`}>
              {message}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-bold text-white/50 mb-1.5 uppercase tracking-wider">First Name</label>
              <input type="text" value={profile.first_name} onChange={e => setProfile({ ...profile, first_name: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                placeholder="John" data-testid="profile-first-name" />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-white/50 mb-1.5 uppercase tracking-wider">Last Name</label>
              <input type="text" value={profile.last_name} onChange={e => setProfile({ ...profile, last_name: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                placeholder="Doe" data-testid="profile-last-name" />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold text-white/50 mb-1.5 uppercase tracking-wider">Birthday</label>
            <input type="date" value={profile.birthday} onChange={e => setProfile({ ...profile, birthday: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
              data-testid="profile-birthday" />
          </div>

          <div>
            <label className="block text-[10px] font-bold text-white/50 mb-1.5 uppercase tracking-wider">Phone Number</label>
            <input type="tel" value={profile.phone} onChange={e => setProfile({ ...profile, phone: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
              placeholder="+1 (555) 123-4567" data-testid="profile-phone" />
          </div>

          <div>
            <label className="block text-[10px] font-bold text-white/50 mb-1.5 uppercase tracking-wider">City / Address</label>
            <input type="text" value={profile.city} onChange={e => setProfile({ ...profile, city: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
              placeholder="New York, NY" data-testid="profile-city" />
          </div>

          <button type="submit" disabled={saving}
            className="w-full py-3.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-extrabold rounded-xl hover:brightness-110 transition-all disabled:opacity-50 text-sm"
            data-testid="save-profile-button">
            {saving ? 'Saving...' : 'SAVE PROFILE'}
          </button>
        </form>

        {/* Settings Section */}
        <div className="mt-6 bg-white/5 rounded-2xl border border-white/10 p-6" data-testid="user-settings">
          <h2 className="text-sm font-extrabold text-white mb-4 uppercase tracking-wider">Settings</h2>

          {/* Subscription Status */}
          <div className="space-y-3">
            <div className="py-3 border-b border-white/5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <svg className="w-4 h-4 text-emerald-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                  </div>
                  <div>
                    <p className="text-white text-sm font-bold">Subscription</p>
                    <p className="text-white/30 text-xs">
                      {subscription?.status === 'active' ? 'VIP Active' : subscription?.status === 'cancelled' ? 'VIP (Cancelling — access until period ends)' : 'Free Plan'}
                    </p>
                  </div>
                </div>
                {subscription?.status === 'active' || subscription?.status === 'cancelled' ? (
                  <span className="bg-emerald-500/20 text-emerald-400 px-2.5 py-1 rounded-lg text-[10px] font-extrabold border border-emerald-500/30">VIP</span>
                ) : (
                  <button onClick={() => navigate('/dashboard')} className="bg-emerald-500 text-white px-3 py-1.5 rounded-lg text-xs font-extrabold hover:bg-emerald-600 transition-all">
                    Upgrade
                  </button>
                )}
              </div>
              {/* Cancel Subscription Button */}
              {subscription?.status === 'active' && (
                <div className="mt-3 pl-11">
                  <button
                    onClick={async () => {
                      if (!window.confirm('Are you sure you want to cancel your VIP subscription? You will still have access until the end of your current billing period.')) return;
                      setCancellingSubscription(true);
                      try {
                        const res = await axios.post(`${API}/subscription/cancel`);
                        setSubscription({ ...subscription, status: 'cancelled' });
                        alert(res.data.message || 'Subscription cancelled successfully.');
                      } catch (err) {
                        alert(err.response?.data?.detail || 'Failed to cancel subscription.');
                      } finally {
                        setCancellingSubscription(false);
                      }
                    }}
                    disabled={cancellingSubscription}
                    className="w-full py-2.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 hover:border-red-500/30 font-bold rounded-lg transition-all disabled:opacity-50 text-xs"
                    data-testid="cancel-subscription-button"
                  >
                    {cancellingSubscription ? 'Cancelling...' : 'Cancel Subscription'}
                  </button>
                  <p className="text-white/20 text-[10px] mt-1.5 text-center">You keep access until the end of your billing period</p>
                </div>
              )}
              {subscription?.status === 'cancelled' && (
                <p className="mt-2 pl-11 text-orange-400/60 text-xs">Your subscription is cancelled. You keep VIP access until the billing period ends.</p>
              )}
            </div>

            {/* Change Password */}
            <div className="py-3 border-b border-white/5">
              <button onClick={() => { setShowChangePassword(!showChangePassword); setPasswordMessage(''); }} className="flex items-center justify-between w-full group" data-testid="change-password-toggle">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center">
                    <svg className="w-4 h-4 text-orange-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
                  </div>
                  <div className="text-left">
                    <p className="text-white text-sm font-bold">Change Password</p>
                    <p className="text-white/30 text-xs">Update your account password</p>
                  </div>
                </div>
                <svg className={`w-4 h-4 text-white/20 group-hover:text-white/40 transition-all ${showChangePassword ? 'rotate-90' : ''}`} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
              </button>

              {showChangePassword && (
                <form onSubmit={handleChangePassword} className="mt-4 space-y-3 pl-11" data-testid="change-password-form">
                  {passwordMessage && (
                    <div className={`px-3 py-2 rounded-lg text-xs font-bold ${passwordMessage.includes('successfully') ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`} data-testid="password-message">
                      {passwordMessage}
                    </div>
                  )}
                  <input type="password" placeholder="Current password" required value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                    data-testid="current-password-input" />
                  <input type="password" placeholder="New password (min 6 chars)" required value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                    data-testid="new-password-input" />
                  <input type="password" placeholder="Confirm new password" required value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                    data-testid="confirm-password-input" />
                  <button type="submit" disabled={passwordLoading}
                    className="w-full py-2.5 bg-orange-500/80 hover:bg-orange-500 text-white font-extrabold rounded-lg transition-all disabled:opacity-50 text-sm"
                    data-testid="change-password-submit">
                    {passwordLoading ? 'Changing...' : 'Update Password'}
                  </button>
                </form>
              )}
            </div>

            {/* Telegram */}
            {settings?.telegram_link && (
              <a href={settings.telegram_link} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between py-3 border-b border-white/5 group" data-testid="settings-telegram">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/></svg>
                  </div>
                  <div>
                    <p className="text-white text-sm font-bold">Telegram Channel</p>
                    <p className="text-white/30 text-xs">Join for updates</p>
                  </div>
                </div>
                <svg className="w-4 h-4 text-white/20 group-hover:text-white/40 transition-colors" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
              </a>
            )}

            {/* Support */}
            <a href="mailto:support@sportspredictionstips.com" className="flex items-center justify-between py-3 border-b border-white/5 group" data-testid="settings-support">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <svg className="w-4 h-4 text-purple-400" fill="currentColor" viewBox="0 0 20 20"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" /><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" /></svg>
                </div>
                <div>
                  <p className="text-white text-sm font-bold">Support</p>
                  <p className="text-white/30 text-xs">support@sportspredictionstips.com</p>
                </div>
              </div>
              <svg className="w-4 h-4 text-white/20 group-hover:text-white/40 transition-colors" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
            </a>

            {/* Terms */}
            <button onClick={() => navigate('/terms')} className="flex items-center justify-between py-3 w-full group" data-testid="settings-terms">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                  <svg className="w-4 h-4 text-white/40" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" /></svg>
                </div>
                <div className="text-left">
                  <p className="text-white text-sm font-bold">Terms & Privacy</p>
                  <p className="text-white/30 text-xs">Legal information</p>
                </div>
              </div>
              <svg className="w-4 h-4 text-white/20 group-hover:text-white/40 transition-colors" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;
